import { Flex, Text, TextField, Select } from '@radix-ui/themes'
import { GameType } from '@Generated/back-office-client/src'
import { produce } from 'immer'
import { GameMultiselect } from '../components/game-multiselect'
import { GameOption, PromotionCampaignFormData } from '../types'
import { GAME_TYPE_OPTIONS } from '../constants'
import '../styles.scss'
import { convertDollarsToCents } from '../utils'

type RequirementsSectionProps = {
  campaign: PromotionCampaignFormData
  canEdit: boolean
  gameOptions: GameOption[]
  onChange: (campaign: PromotionCampaignFormData) => void
}

const hasInvalidWinAmounts = (campaign: PromotionCampaignFormData): boolean => {
  const min = campaign.filters?.winAmountMin
  const max = campaign.filters?.winAmountMax
  return min !== undefined && max !== undefined && min > max
}

export const RequirementsSection = ({
  campaign,
  canEdit,
  gameOptions,
  onChange,
}: RequirementsSectionProps) => {
  return (
    <Flex direction="column" gap="3">
      <Text size="3" weight="bold">
        Requirements
      </Text>

      <Flex direction="column" gap="3" className="requirements-container">
        <Flex gap="3">
          <Flex direction="column" gap="2" style={{ flex: 1 }}>
            <Text size="2" weight="medium">
              Quantity
            </Text>
            <TextField.Root
              type="number"
              min="1"
              disabled={!canEdit}
              value={campaign.triggerCount || ''}
              onChange={(e) => {
                const parsed = parseInt(e.target.value, 10)
                onChange({ ...campaign, triggerCount: isNaN(parsed) ? 0 : parsed })
              }}
            />
            {(!campaign.triggerCount || campaign.triggerCount < 1) && (
              <Text size="1" color="red">
                Quantity must be at least 1
              </Text>
            )}
          </Flex>

          <Flex direction="column" gap="2" style={{ flex: 1 }}>
            <Text size="2" weight="medium">
              Game Type
            </Text>
            <Select.Root
              disabled={!canEdit}
              value={campaign.filters?.gameType || 'ANY'}
              onValueChange={(value) =>
                onChange(
                  produce(campaign, (updated: PromotionCampaignFormData) => {
                    if (!updated.filters) {
                      updated.filters = {}
                    }
                    updated.filters.gameType = value === 'ANY' ? undefined : (value as GameType)
                  }),
                )
              }>
              <Select.Trigger />
              <Select.Content>
                {GAME_TYPE_OPTIONS.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>
        </Flex>

        <Flex direction="column" gap="2">
          <Text size="2" weight="medium">
            Game ID
          </Text>
          <GameMultiselect
            gameOptions={gameOptions}
            selectedGameIds={campaign.filters?.gameIds || []}
            disabled={!canEdit}
            onChange={(selectedIds) =>
              onChange(
                produce(campaign, (updated: PromotionCampaignFormData) => {
                  if (!updated.filters) {
                    updated.filters = {}
                  }
                  updated.filters.gameIds = selectedIds.length > 0 ? selectedIds : undefined
                }),
              )
            }
          />
          <Text size="1" color="gray">
            Search and select multiple games, or leave empty for all games
          </Text>
        </Flex>

        <Flex gap="3">
          <Flex direction="column" gap="2" style={{ flex: 1 }}>
            <Text size="2" weight="medium">
              Min Win Amount
            </Text>
            <TextField.Root
              type="number"
              step="0.01"
              min="0"
              placeholder="Enter minimum (e.g. $5.00)"
              disabled={!canEdit}
              value={
                campaign.filters?.winAmountMin !== undefined
                  ? campaign.filters.winAmountMin / 100
                  : ''
              }
              onChange={(e) =>
                onChange(
                  produce(campaign, (updated: PromotionCampaignFormData) => {
                    if (!updated.filters) {
                      updated.filters = {}
                    }
                    updated.filters.winAmountMin =
                      e.target.value !== '' ? convertDollarsToCents(e.target.value) : undefined
                  }),
                )
              }>
              <TextField.Slot>$</TextField.Slot>
            </TextField.Root>
          </Flex>

          <Flex direction="column" gap="2" style={{ flex: 1 }}>
            <Text size="2" weight="medium">
              Max Win Amount
            </Text>
            <TextField.Root
              type="number"
              step="0.01"
              min="0"
              placeholder="Enter maximum (e.g. $5.00)"
              disabled={!canEdit}
              value={
                campaign.filters?.winAmountMax !== undefined
                  ? campaign.filters.winAmountMax / 100
                  : ''
              }
              onChange={(e) =>
                onChange(
                  produce(campaign, (updated: PromotionCampaignFormData) => {
                    if (!updated.filters) {
                      updated.filters = {}
                    }
                    updated.filters.winAmountMax =
                      e.target.value !== '' ? convertDollarsToCents(e.target.value) : undefined
                  }),
                )
              }>
              <TextField.Slot>$</TextField.Slot>
            </TextField.Root>
          </Flex>
        </Flex>
        <Text size="1" color="gray">
          To create a promotion for non-winners, set the Max Win Amount to $0.
        </Text>

        {hasInvalidWinAmounts(campaign) && (
          <Text size="2" color="red" weight="medium">
            Minimum win amount cannot be greater than maximum win amount
          </Text>
        )}
      </Flex>
    </Flex>
  )
}
